'use client';

import { JSFunction } from '@ahm/common-helpers';
import { useEffect, useMemo, useState } from 'react';
import { DetectWebinappContext } from './use-detect-webinapp';
import { useIsMobile } from '@/lib/is-mobile';

export interface DetectWebinappProviderProps {
  children: React.ReactNode;
  isWebinapp?: boolean;
}

export const WEBINAPP_STORAGE_KEY = 'isWebinapp';

export function DetectWebinappProvider({
  children,
  isWebinapp: ssrValue = false,
}: DetectWebinappProviderProps) {
  const isMobile = useIsMobile();
  const [isWebinapp, setIsWebinapp] = useState(ssrValue);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Detection chạy MỘT lần cho cả app (không lặp ở mỗi consumer như trước).
    // Đọc localStorage + URL `?webinapp=1`. Nếu match (và mobile), set state +
    // gọi native `hide_toolbar` MỘT lần, rồi dọn URL param.
    const storedWebInApp =
      localStorage.getItem(WEBINAPP_STORAGE_KEY) === 'true';
    const urlParams = new URLSearchParams(window.location.search);
    const hasWebInAppParam = urlParams.has('webinapp');

    if ((hasWebInAppParam || storedWebInApp) && isMobile) {
      if (!storedWebInApp) {
        localStorage.setItem(WEBINAPP_STORAGE_KEY, 'true');
      }
      setIsWebinapp(true);
      JSFunction.call({ name: 'hide_toolbar' });

      if (hasWebInAppParam) {
        // Dọn URL sau khi đã ghi nhận, tránh duplicate trigger từ shared link
        setTimeout(() => {
          try {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.delete('webinapp');
            window.history.replaceState({}, '', currentUrl.toString());
          } catch (error) {
            console.error('Error removing webinapp parameter:', error);
          }
        }, 1000);
      }
    }
    setHydrated(true);
  }, [isMobile]);

  // Memoize value để consumers chỉ re-render khi isWebinapp/hydrated thực sự đổi
  // (tránh new object identity mỗi lần provider re-render do parent re-render).
  const value = useMemo(
    () => ({ isWebinapp, hydrated }),
    [isWebinapp, hydrated]
  );

  return (
    <DetectWebinappContext.Provider value={value}>
      {children}
    </DetectWebinappContext.Provider>
  );
}
